import { useEffect, useState, type ReactNode } from 'react'
import { useAudit } from '@/hooks/useAudit'
import { downloadAuditXlsx, type AuditFilters } from '@/lib/api/audit'
import { auditSummary, formatDetailJson } from '@/lib/audit-format'
import type { AuditEvent } from '@/lib/schemas/audit'

const PAGE_SIZE = 50

const TH =
  'px-3 py-2 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase whitespace-nowrap'

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

export default function Audit() {
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [eventType, setEventType] = useState('')
  const [userInput, setUserInput] = useState('')
  const [orderInput, setOrderInput] = useState('')
  const [qInput, setQInput] = useState('')
  const [page, setPage] = useState(0)

  const user = useDebounced(userInput, 300)
  const orderNumber = useDebounced(orderInput, 300)
  const q = useDebounced(qInput, 300)

  // Any filter change returns to the first page. Comparing a derived key during
  // render is React's recommended alternative to a reset-in-effect.
  const filterKey = `${start}|${end}|${eventType}|${user}|${orderNumber}|${q}`
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey)
    setPage(0)
  }

  const filters: AuditFilters = {
    start: start || undefined,
    end: end || undefined,
    event_type: eventType || undefined,
    user: user || undefined,
    order_number: orderNumber || undefined,
    q: q || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }

  const query = useAudit(filters)
  const data = query.data
  const events = data?.events ?? []
  const total = data?.total ?? 0
  const eventTypes = data?.event_types ?? []

  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, total)
  const hasPrev = page > 0
  const hasNext = rangeEnd < total

  function reset() {
    setStart('')
    setEnd('')
    setEventType('')
    setUserInput('')
    setOrderInput('')
    setQInput('')
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">Audit log</h1>
          {query.isFetching && !query.isLoading && (
            <span className="text-xs text-gray-400">Loading…</span>
          )}
        </div>
        <ExportButton filters={filters} />
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 border border-gray-200 p-3">
        <FilterField label="From" htmlFor="audit-start">
          <input
            id="audit-start"
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="border border-gray-300 px-2 py-1 text-sm"
          />
        </FilterField>
        <FilterField label="To" htmlFor="audit-end">
          <input
            id="audit-end"
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="border border-gray-300 px-2 py-1 text-sm"
          />
        </FilterField>
        <FilterField label="Event type" htmlFor="audit-type">
          <select
            id="audit-type"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="">All events</option>
            {eventTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="User" htmlFor="audit-user">
          <input
            id="audit-user"
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder="email"
            className="border border-gray-300 px-2 py-1 text-sm"
          />
        </FilterField>
        <FilterField label="Order #" htmlFor="audit-order">
          <input
            id="audit-order"
            type="text"
            value={orderInput}
            onChange={(e) => setOrderInput(e.target.value)}
            className="border border-gray-300 px-2 py-1 text-sm tabular-nums"
          />
        </FilterField>
        <FilterField label="Search" htmlFor="audit-q">
          <input
            id="audit-q"
            type="search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="order # or product"
            className="border border-gray-300 px-2 py-1 text-sm"
          />
        </FilterField>
        <button
          type="button"
          onClick={reset}
          className="border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:text-black"
        >
          Reset
        </button>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-gray-500">Loading audit log…</p>
      ) : query.isError ? (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-red-600">
            {query.error instanceof Error ? query.error.message : 'Failed to load audit log.'}
          </p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="border border-gray-800 px-3 py-1 text-sm hover:bg-gray-900 hover:text-white"
          >
            Retry
          </button>
        </div>
      ) : events.length === 0 ? (
        <p className="text-sm text-gray-500">No events match these filters.</p>
      ) : (
        <>
          <div className="overflow-x-auto border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className={TH}>Timestamp</th>
                  <th className={TH}>Event</th>
                  <th className={TH}>User</th>
                  <th className={TH}>Order</th>
                  <th className={TH}>Summary</th>
                  <th className={TH}>
                    <span className="sr-only">Detail</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <EventRows key={event.id} event={event} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-gray-500 tabular-nums">
              {rangeStart}–{rangeEnd} of {total}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={!hasPrev}
                className="border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:text-black disabled:cursor-not-allowed disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasNext}
                className="border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:text-black disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function FilterField({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: ReactNode
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="block text-xs text-gray-500">
        {label}
      </label>
      {children}
    </div>
  )
}

function EventRows({ event }: { event: AuditEvent }) {
  const [open, setOpen] = useState(false)
  const detailJson = formatDetailJson(event.detail)
  const badgeClass = event.success
    ? 'border-gray-300 text-gray-700'
    : 'border-red-300 bg-red-50 text-red-700'

  return (
    <>
      <tr className="border-b border-gray-100 align-top last:border-b-0">
        <td className="px-3 py-2 whitespace-nowrap tabular-nums text-gray-600">
          {formatDateTime(event.created_at)}
        </td>
        <td className="px-3 py-2">
          <span className={`inline-block border px-1.5 py-0.5 font-mono text-xs ${badgeClass}`}>
            {event.event_type}
          </span>
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          {event.user_email ?? <span className="text-gray-400">—</span>}
        </td>
        <td className="px-3 py-2 whitespace-nowrap tabular-nums">
          {event.shopify_order_id ?? <span className="text-gray-400">—</span>}
        </td>
        <td className="px-3 py-2">
          <span className="block max-w-md truncate text-gray-700" title={auditSummary(event)}>
            {auditSummary(event)}
          </span>
        </td>
        <td className="px-3 py-2 text-right">
          {detailJson && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className="text-xs text-gray-500 hover:text-black"
            >
              {open ? 'Hide' : 'Detail'}
            </button>
          )}
        </td>
      </tr>
      {open && detailJson && (
        <tr className="border-b border-gray-100">
          <td colSpan={6} className="bg-gray-50 px-3 py-2">
            <pre className="overflow-x-auto text-xs text-gray-700">{detailJson}</pre>
          </td>
        </tr>
      )}
    </>
  )
}

function ExportButton({ filters }: { filters: AuditFilters }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function click() {
    setBusy(true)
    setError(null)
    try {
      await downloadAuditXlsx(filters)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-1 text-right">
      <button
        type="button"
        onClick={() => void click()}
        disabled={busy}
        className="border border-gray-800 px-3 py-1 text-sm hover:bg-gray-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? 'Exporting…' : 'Export to Excel'}
      </button>
      {error && (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  )
}
